import React, {useCallback, useEffect, useState, useRef} from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  Text,
  RefreshControl,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import {Header} from '../components/Header';
import {ArticleCard} from '../components/ArticleCard';
import {AdBanner} from '../components/AdBanner';
import {BackToTopButton} from '../components/BackToTopButton';
import {useArticles} from '../hooks/useArticles';
import {useTheme} from '../hooks/useTheme';
import {useArticleNavigation, hasMatchId} from '../hooks/useArticleNavigation';
import {Article} from '../types/article';
import {fetchAds, Ad} from '../services/adService';

// Interval for inserting ads in the list
const AD_INTERVAL = 7;
const SCROLL_THRESHOLD = 300; // Show back-to-top after 300px scroll

type ListItem = 
  | {type: 'article'; data: Article; index: number} 
  | {type: 'ad'; slot: number; ad: Ad | null};



export const HomeScreen: React.FC = () => {
  const {
    data,
    isLoading,
    error,
    refetch,
    isRefetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useArticles();
  const {colors} = useTheme();

  const flatListRef = useRef<FlatList>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  // Ads state
  const [ads, setAds] = useState<Ad[]>([]);
  const [adsLoaded, setAdsLoaded] = useState(false);
  
  // Fetch ads on mount and refresh
  const loadAds = useCallback(async () => {
    const fetchedAds = await fetchAds(15); // Fetch 15 ads (1 header + 14 middle positions)
    setAds(fetchedAds);
    setAdsLoaded(true);
  }, []);
  
  useEffect(() => {
    loadAds();
  }, [loadAds]);

  // Track scroll position for back-to-top button
  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    setShowBackToTop(offsetY > SCROLL_THRESHOLD);
  }, []);

  const handleBackToTop = useCallback(() => {
    flatListRef.current?.scrollToOffset({offset: 0, animated: true});
  }, []);

  // Flatten pages into a single array of articles
  const articles = React.useMemo(() => {
    return data?.pages.flatMap(page => page.posts) || [];
  }, [data]);

  // Use shared article navigation hook with DIRETTA support
  const {handleArticlePress} = useArticleNavigation(articles);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  
  // Get ad by slot index
  const getAdForSlot = useCallback((slot: number): Ad | null => {
    return ads.find(ad => ad.slot === slot) || null;
  }, [ads]);

  // Build list with ads inserted
  const getListData = useCallback((): ListItem[] => {
    if (!articles.length) return [];
    
    const items: ListItem[] = [];
    let adSlot = 0;
    
    // Check if any of the first 6 articles is DIRETTA with a match_id (for featured display)
    const top6 = articles.slice(0, 6);
    const direttaIndex = top6.findIndex(
      a => a.category?.toLowerCase() === 'diretta' && hasMatchId(a)
    );
    const hasDirettaFeatured = direttaIndex !== -1;
    const direttaArticle = hasDirettaFeatured ? articles[direttaIndex] : null;
    
    // Build reordered article list: diretta first (if found), then the rest
    let orderedArticles: Article[];
    if (hasDirettaFeatured && direttaArticle) {
      orderedArticles = [
        direttaArticle,
        ...articles.filter((_, i) => i !== direttaIndex),
      ];
    } else {
      orderedArticles = articles;
    }
    
    orderedArticles.forEach((article, index) => {
      if (index === 0) {
        if (hasDirettaFeatured) {
          // DIRETTA with match_id: Show as featured card above the ad
          items.push({type: 'article', data: article, index});
          items.push({type: 'ad', slot: adSlot, ad: getAdForSlot(adSlot)});
          adSlot++;
          return;
        } else {
          // No DIRETTA in top 6: Show ad first, then normal article
          items.push({type: 'ad', slot: adSlot, ad: getAdForSlot(adSlot)});
          adSlot++;
        }
      }
      
      items.push({type: 'article', data: article, index});
      
      // Insert ad every AD_INTERVAL articles (after 5th, 10th, etc.)
      if ((index + 1) % AD_INTERVAL === 0 && index < orderedArticles.length - 1) {
        items.push({type: 'ad', slot: adSlot, ad: getAdForSlot(adSlot)});
        adSlot++;
      }
    });
    
    return items;
  }, [articles, getAdForSlot]);

  const renderItem = useCallback(
    ({item, index: listIndex}: {item: ListItem; index: number}) => {
      if (item.type === 'ad') {
        // Header ad (slot 0): always show placeholder to prevent layout jump
        if (item.slot === 0) {
          return (
            <AdBanner 
              position="top" 
              imageUrl={item.ad?.image_url || undefined}
              targetUrl={item.ad?.target_url || undefined}
              showPlaceholder={!adsLoaded}
            />
          );
        }
        // Skip other slots if no ad available
        if (!item.ad) {
          return null;
        }
        return (
          <AdBanner 
            position="middle" 
            imageUrl={item.ad.image_url || undefined}
            targetUrl={item.ad.target_url || undefined}
          />
        );
      }
      
      // Featured if it's the first item in the list and is DIRETTA with match_id
      const isDirettaFeatured = listIndex === 0 && 
        item.data.category?.toLowerCase() === 'diretta' &&
        hasMatchId(item.data);
      
      return (
        <ArticleCard
          article={item.data}
          onPress={() => handleArticlePress(item.data, item.index)}
          featured={isDirettaFeatured}
        />
      );
    },
    [handleArticlePress, adsLoaded],
  );

  const keyExtractor = useCallback((item: ListItem, _index: number) => {
    if (item.type === 'ad') {
      return `ad-slot-${item.slot}`;
    }
    return `article-${item.data.id}-${_index}`;
  }, []);

  const renderFooter = () => (
    <View style={styles.footerSpace}>
      {isFetchingNextPage && (
        <ActivityIndicator 
          size="small" 
          color={colors.primary} 
          style={{marginBottom: 20}} 
        />
      )}
    </View>
  );
  
  // Handle refresh - reload ads too
  const handleRefresh = useCallback(async () => {
    await Promise.all([refetch(), loadAds()]);
  }, [refetch, loadAds]);

  // Dynamic styles based on theme
  const dynamicStyles = {
    container: {
      backgroundColor: colors.background,
    },
    loadingText: {
      color: colors.textSecondary,
    },
    errorText: {
      color: colors.error,
    },
    errorDetail: {
      color: colors.textSecondary,
    },
  };

  if (isLoading) {
    return (
      <View style={[styles.container, dynamicStyles.container]}>
        <Header />
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={[styles.loadingText, dynamicStyles.loadingText]}>Caricamento articoli...</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.container, dynamicStyles.container]}>
        <Header />
        <View style={styles.centered}>
          <Text style={[styles.errorText, dynamicStyles.errorText]}>Errore nel caricamento</Text>
          <Text style={[styles.errorDetail, dynamicStyles.errorDetail]}>{error.message}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, dynamicStyles.container]}>
      <Header />
      
      {/* Main Article List */}
      <FlatList
        ref={flatListRef}
        data={getListData()}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        contentContainerStyle={styles.list}
        ListFooterComponent={renderFooter}
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
        showsVerticalScrollIndicator={false}
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.5}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      />
      <BackToTopButton
        visible={showBackToTop}
        onPress={handleBackToTop}
        bottomOffset={100}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  list: {
    paddingVertical: 4,
  },
  footerSpace: {
    paddingBottom: 100, // Space for floating tab bar
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  errorText: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  errorDetail: {
    fontSize: 14,
    textAlign: 'center',
  },
});
